/**
 * Perpendicular point calculation for polyline entities.
 *
 * OdDbPolyline.getOsnapPoints() returns 0 points for kOsModePerp in this
 * WASM build (confirmed empirically - see SnapManager.ts), so this mirrors
 * the pattern used for "nearest" (src/drawing/snap/nearest/polyline.ts):
 * iterate each segment and compute the geometry manually.
 *
 * Only straight (kLine) segments are handled. Arc segments are skipped -
 * computing the perpendicular foot on an arc requires reliably checking
 * whether the candidate point falls within the arc's angular range, which
 * needs verification against a running app that isn't available right now.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DrawingModuleType } from "../../DrawingSingleton";
import { deleteObj } from "../../core/delete";
import { SegType } from "../../core/constants";
import type { Point3D } from "../../interaction/types";

/**
 * Find perpendicular foot points from `fromPoint` onto each straight segment
 * of a polyline. A foot is only returned when it falls within the segment's
 * extent - perpendicular osnap shouldn't extend past a finite segment's
 * endpoints.
 *
 * @param module The ODA drawing module
 * @param polyline The polyline entity
 * @param fromPoint The reference point to project perpendicular from (e.g.
 *   the last committed vertex of the shape currently being drawn)
 * @returns Candidate perpendicular points on the polyline (may be empty)
 */
export function getPerpendicularPointsOnPolyline(
  module: DrawingModuleType,
  polyline: DrawingModule.OdDbPolyline,
  fromPoint: Point3D,
): Point3D[] {
  const numVerts = polyline.numVerts();
  if (numVerts === 0) return [];

  const isClosed = polyline.isClosed();
  const numSegs = isClosed ? numVerts : numVerts - 1;
  if (numSegs === 0) return [];

  const points: Point3D[] = [];

  for (let i = 0; i < numSegs; i++) {
    const segType = polyline.segType(i);
    if (segType !== SegType.kLine) continue;

    const lineSeg = new module.OdGeLineSeg2d();
    polyline.getLineSegAt(i, lineSeg);
    const startPt = lineSeg.startPoint();
    const endPt = lineSeg.endPoint();

    const dx = endPt.x - startPt.x;
    const dy = endPt.y - startPt.y;
    const lenSq = dx * dx + dy * dy;

    if (lenSq > 1e-12) {
      const t =
        ((fromPoint.x - startPt.x) * dx + (fromPoint.y - startPt.y) * dy) /
        lenSq;
      if (t >= 0 && t <= 1) {
        points.push({
          x: startPt.x + t * dx,
          y: startPt.y + t * dy,
          z: 0,
        });
      }
    }

    deleteObj(startPt);
    deleteObj(endPt);
    deleteObj(lineSeg);
  }

  return points;
}
